import moment from "moment-timezone";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip as ReactTooltip } from "react-tooltip";
import {
  dayHourBars,
  formatMetric,
  heatPercent,
  heatRows,
  hourLabel,
  hourSlot,
  hourTick,
  peakRank,
  WEEKDAYS_LONG,
} from "../busy-times-utils";
import QuickToggle from "./QuickToggle";

const RAMP = [12, 30, 50, 72, 100];

const boldDay = (text, day) => text.replace(day, `<b>${day}</b>`);

/** The cell's tooltip as HTML: bold day and hour, peak rank on its own line. */
const cellTip = ({ row, item, busy, t }) => {
  const day = `${row.long} ${hourSlot(item.hour)}`;
  if (item.noData)
    return boldDay(t("doorAccess.busy.cellNoData", { day }), day);
  const unit = t(`doorAccess.busy.unit.${busy.metric}`);
  let tip = t("doorAccess.busy.cellTip", {
    day,
    value: formatMetric(item.value, busy.metric),
    unit,
  });
  if (busy.metric === "average" && item.cell?.busiest) {
    tip += ` · ${t("doorAccess.busy.cellBusiest", {
      date: moment.utc(item.cell.busiest.date).format("MMM D"),
      visits: item.cell.busiest.visits,
    })}`;
  }
  const rank = item.cell ? peakRank(busy, item.cell) : 0;
  const html = boldDay(tip, day);
  return rank ? `${html}<br>${t("doorAccess.busy.peakRank", { rank })}` : html;
};

const DayHours = ({ busy, weekday, onClose, t }) => {
  const bars = dayHourBars(busy, weekday);
  const day = WEEKDAYS_LONG[weekday - 1];
  const width = 1000;
  const height = 120;
  const slot = width / bars.length;
  const unit = t(`doorAccess.busy.unit.${busy.metric}`);
  return (
    <div className="door-busy-day">
      <div className="door-busy-day__head">
        <b>{t("doorAccess.busy.dayByHour", { day })}</b>
        <span className="door-muted">{unit}</span>
        <span className="door-grow" />
        <button
          type="button"
          data-button-type="text"
          sz="small"
          onClick={onClose}
        >
          <span>{t("doorAccess.busy.close")}</span>
        </button>
      </div>
      {busy.weekdayCounts[weekday - 1] === 0 ? (
        <p className="door-muted">{t("doorAccess.busy.dayNoData", { day })}</p>
      ) : (
        <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={day}>
          {bars.map((bar, index) => {
            const barHeight = bar.ratio * (height - 24);
            return (
              <g key={bar.hour}>
                <title>{`${hourLabel(bar.hour)}: ${formatMetric(bar.value, busy.metric)}`}</title>
                <rect
                  x={index * slot + slot * 0.18}
                  y={height - 18 - barHeight}
                  width={slot * 0.64}
                  height={barHeight}
                  rx="3"
                />
                <text
                  x={index * slot + slot / 2}
                  y={height - 4}
                  textAnchor="middle"
                >
                  {hourTick(bar.hour)}
                </text>
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
};

/**
 * Day x hour heatmap of the selected metric. The three peak slots are
 * outlined; clicking a weekday opens its hours below the grid.
 */
const BusyHeatmap = ({ busy, caption, onMetric, metrics, waiting = false }) => {
  const { t } = useTranslation("coaching-portal");
  const [day, setDay] = useState(null);
  const rows = heatRows(busy);
  const hours = rows[0].cells.length;
  const max = busy.max;
  const allHours = hours > 18;

  return (
    <div className="door-card door-busy-heat">
      <div className="door-busy-heat__head">
        <div className="door-grow">
          <h3>{t("doorAccess.busy.title")}</h3>
          <p className="door-muted">
            {caption} · {t("doorAccess.busy.clickHint")}
          </p>
        </div>
        <QuickToggle
          label={t("doorAccess.busy.metricLabel")}
          value={busy.metric}
          options={metrics.map((metric) => ({
            value: metric,
            label: t(`doorAccess.busy.metric.${metric}`),
          }))}
          onSelect={onMetric}
        />
      </div>

      <div className="door-heat-wrap">
        <div className="door-busy-scroll">
          <div
            className="door-heat"
            style={{
              gridTemplateColumns: `3rem repeat(${hours}, minmax(1.75rem, 1fr))`,
            }}
          >
            <span />
            {rows[0].cells.map((item) => (
              <span key={item.hour} className="door-heat__hour">
                {hourTick(item.hour)}
              </span>
            ))}
            {rows.map((row) => [
              <button
                key={`${row.weekday}-label`}
                type="button"
                className="door-heat__day"
                data-active={day === row.weekday || undefined}
                onClick={() => setDay(day === row.weekday ? null : row.weekday)}
              >
                {row.label}
              </button>,
              ...row.cells.map((item) => {
                const percent = item.noData ? 0 : heatPercent(item.value, max);
                return (
                  <span
                    key={`${row.weekday}-${item.hour}`}
                    className="door-heat__cell"
                    data-peak={item.peak || undefined}
                    data-strong={percent > 55 || undefined}
                    data-none={item.noData || undefined}
                    data-tooltip-id={waiting ? undefined : "door-heat-tip"}
                    data-tooltip-html={
                      waiting ? undefined : cellTip({ row, item, busy, t })
                    }
                    style={
                      percent
                        ? {
                            background: `color-mix(in srgb, var(--information) ${percent}%, transparent)`,
                          }
                        : undefined
                    }
                  >
                    {item.peak && !allHours && (
                      <span className="door-heat__tag">
                        {t("doorAccess.busy.peakTag")}
                      </span>
                    )}
                    {item.value > 0 && !item.noData
                      ? formatMetric(item.value, busy.metric)
                      : ""}
                  </span>
                );
              }),
            ])}
          </div>
        </div>

        {waiting && (
          <div className="door-heat__waiting">
            <div>
              <b>{t("doorAccess.states.waitingTitle")}</b>
              <p>{t("doorAccess.busy.waitingText")}</p>
            </div>
          </div>
        )}
      </div>

      <ReactTooltip
        id="door-heat-tip"
        float
        positionStrategy="fixed"
        className="React-Tooltip tooltip-default-style custom-style z-50"
        globalCloseEvents={{ scroll: true }}
      />

      {!waiting && (
        <div className="door-heat__legend">
          <span>0</span>
          <span className="door-heat__ramp" aria-hidden="true">
            {RAMP.map((percent) => (
              <i
                key={percent}
                style={{
                  background: `color-mix(in srgb, var(--information) ${percent}%, transparent)`,
                }}
              />
            ))}
          </span>
          <span>
            {formatMetric(max, busy.metric)}{" "}
            {t(`doorAccess.busy.unit.${busy.metric}`)}
          </span>
          <span className="door-heat__key" data-kind="peak">
            <i />
            {t("doorAccess.busy.legendPeak")}
          </span>
          {rows.some((row) => row.noData) && (
            <span className="door-heat__key" data-kind="none">
              <i />
              {t("doorAccess.busy.legendNoData")}
            </span>
          )}
        </div>
      )}

      {day && !waiting && (
        <DayHours
          busy={busy}
          weekday={day}
          t={t}
          onClose={() => setDay(null)}
        />
      )}
    </div>
  );
};

export default BusyHeatmap;
